import type { Job, V3 } from "@istari/istari-client";
import type { QueryClient } from "@tanstack/react-query";

import { jobsQueryKeys } from "~/jobs/data-source/jobs-query-options";
import { applyEventToJob, isTerminalStatus } from "~/jobs/model/job-events";

type ItemsPage = { items?: Job[] | null };
type InfiniteContainer = { pages?: ItemsPage[] };

// Clearing (vs evicting one) is fine: the client cursor advances past delivered events, so a
// redelivery that would re-hit this set is rare, and re-applying an event is idempotent anyway.
const SEEN_BOUND = 2000;

function patchItemsPage(page: ItemsPage, jobId: string, event: V3.JobStatusChangedEvent): ItemsPage {
  const items = page.items ?? [];
  const index = items.findIndex((job) => job.id === jobId);
  if (index === -1) return page;
  const updated = applyEventToJob(items[index], event);
  if (updated === items[index]) return page;
  const next = items.slice();
  next[index] = updated;
  return { ...page, items: next };
}

function patchJobContainer(data: unknown, jobId: string, event: V3.JobStatusChangedEvent): unknown {
  if (!data || typeof data !== "object") return data;

  if (Array.isArray((data as InfiniteContainer).pages)) {
    const container = data as InfiniteContainer;
    let changed = false;
    const pages = container.pages!.map((page) => {
      const patched = patchItemsPage(page, jobId, event);
      if (patched !== page) changed = true;
      return patched;
    });
    return changed ? { ...container, pages } : data;
  }

  if (Array.isArray((data as ItemsPage).items)) {
    return patchItemsPage(data as ItemsPage, jobId, event);
  }

  if ((data as Job).id === jobId) {
    return applyEventToJob(data as Job, event);
  }

  return data;
}

/**
 * Apply a batch of live job-status events to the React Query caches.
 *
 * Patches the job in every ``["jobs", …]`` list cache (page + infinite shapes) and the single-job
 * ``["job", id]`` cache in place — no refetch. Events are applied oldest-first and deduped on
 * ``eventId`` (the feed can redeliver). Terminal transitions are handed to ``onTerminal`` for the
 * per-event completion side-effects the caller owns (toast/sound, optimistic cleanup).
 *
 * Returns ``true`` when the page contained at least one terminal event, so the caller can run the
 * broad downstream invalidation once per page rather than once per terminal event.
 */
export function applyJobEvents(
  queryClient: QueryClient,
  events: V3.JobStatusChangedEvent[],
  seen: Set<string>,
  onTerminal?: (event: V3.JobStatusChangedEvent) => void,
): boolean {
  const fresh = events
    .filter((event) => !event.eventId || !seen.has(event.eventId))
    .sort((a, b) => (a.createdAt?.getTime() ?? 0) - (b.createdAt?.getTime() ?? 0));

  let hadTerminal = false;
  for (const event of fresh) {
    if (event.eventId) seen.add(event.eventId);

    queryClient.setQueriesData({ queryKey: jobsQueryKeys.all }, (data) => patchJobContainer(data, event.jobId, event));
    queryClient.setQueryData<Job>(jobsQueryKeys.byId(event.jobId), (data) =>
      data ? applyEventToJob(data, event) : data,
    );

    if (isTerminalStatus(event.status)) {
      hadTerminal = true;
      onTerminal?.(event);
    }
  }

  if (seen.size > SEEN_BOUND) seen.clear();
  return hadTerminal;
}
